import { PropTypes } from 'prop-types';
import React from 'react';
import SwiperCore, { Autoplay, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { BREAKPOINTS } from '../../constants/breakpoints';
import { checkRequired, toCamelCase } from '../../utils';
import StoryEnd from './Slides/StoryEnd';
import StoryIntro from './Slides/StoryIntro';
import StoryMappings from './Slides/StoryMappings';

SwiperCore.use([Autoplay, Pagination, Navigation]);

const Story = ({ data, location }) => {
  const hasRequiredFields = checkRequired(
    data.body,
    data.color,
    data.image,
    data.articleTitle,
    data.relatedLinks,
    data.tags,
    data.franchise,
  );
  if (!hasRequiredFields)
    return <div>Required fields missing in Contentful</div>;
  const { body, color } = data;
  const getColor = toCamelCase(color);

  const speed = 333;
  const storyIntro = <StoryIntro data={data} location={location} />;
  const storyEnd = <StoryEnd data={data} location={location} />;

  const storyBody = body
    .map((bodyData, index) => {
      const id = `b${index}`;
      const {
        format,
        internal: { type: typename },
      } = bodyData;

      const props = { bodyData, data, key: id };

      if (typename === 'ContentfulStoryBody03TextImage') {
        if (!format) return null;

        return React.createElement(StoryMappings[typename][format], props);
      }

      return React.createElement(StoryMappings[typename], props);
    })
    .filter((bodyData) => bodyData !== null);

  const slideData = [storyIntro, ...storyBody, storyEnd].map(
    (value, index) => ({ ...value, key: index }),
  );

  return (
    <div className="story-wrapper">
      <Swiper
        autoplay={{
          delay: 5000,
          disableOnInteraction: true,
          stopOnLastSlide: true,
          waitForTransition: false,
        }}
        breakpoints={{
          [BREAKPOINTS.desktopXS]: {
            shortSwipes: false,
            simulateTouch: true,
            threshold: 200,
            touchStartPreventDefault: false,
          },
        }}
        navigation={{
          nextEl: '.story__shim--right',
          prevEl: '.story__shim--left',
        }}
        onInit={(swiper) => {
          const active = swiper.slides[swiper.activeIndex];
          active.classList.add('visible');
          active.children[0].classList.add('transition-in');
          swiper.$el[0].classList.add(getColor);
        }}
        onTransitionStart={(swiper) => {
          if (
            swiper.previousIndex === undefined ||
            swiper.activeIndex === undefined
          ) {
            return;
          }

          // Start
          const prev = swiper.slides[swiper.previousIndex || 0];
          prev.children[0].classList.add('transition-out');

          // Pagination
          // Add active class to trailing bullets in pagination for staggered progress effect
          const bullets = swiper.pagination.$el[0].children;
          for (let i = 0; i < bullets.length; i += 1) {
            if (i <= swiper.activeIndex) {
              bullets[i].className =
                'swiper-pagination-bullet swiper-pagination-bullet-viewed';
            }
          }
          // End -> onTransitionEnd doesn't fire when using virtualTranslate
          setTimeout(() => {
            if (!swiper.destroyed) {
              const active = swiper.slides[swiper.activeIndex];
              active.classList.add('visible');
              active.children[0].classList.add('transition-in');
              prev.classList.remove('visible');
              prev.children[0].classList.remove(
                'transition-in',
                'transition-out',
              );

              // This is to prevent a wierd bug where previousIndex doesnt register
              if (swiper.previousIndex === undefined) {
                prev.classList.add('visible');
                prev.children[0].classList.add('transition-in');
              }
            }
          }, speed);
        }}
        pagination={{
          bulletActiveClass: 'swiper-pagination-bullet-viewed',
          clickable: true,
          type: 'bullets',
        }}
        speed={speed}
        virtualTranslate
      >
        {slideData.map((slideItem) =>
          React.createElement(SwiperSlide, { key: slideItem.key }, slideItem),
        )}
      </Swiper>
    </div>
  );
};

Story.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object,
};

export default Story;
